/**
 * Spec 216 PR1 — Skeleton primitive.
 *
 * Replaces flat "Cargando…" placeholders with a shimmering rectangle
 * matching the size of the content that's about to land. Linear and
 * Notion both load this way; the perceived latency is shorter than
 * a centred spinner because the user already sees the layout settle.
 *
 * Usage:
 *   <Skeleton width="100%" height={64} />
 *   <SkeletonCard /> — preset that mimics a WorkList row.
 *
 * A11y:
 *   - Wraps inside a `role="status"` + `aria-live="polite"` container
 *     when the parent treats the skeleton as a loading announcement.
 *   - Honours `prefers-reduced-motion` via the global override in
 *     `tokens.css` (the shimmer keyframe collapses to a static fill).
 *
 * Bundle impact: the file ships zero runtime deps; the shimmer
 * keyframe is in `Skeleton.css` (tiny). Verified the new chunk costs
 * ~0.4 KB gzip in PR 1 measurement.
 */
import type { CSSProperties } from 'react';

import './Skeleton.css';

interface SkeletonProps {
  /** CSS width — number maps to px, string passes through (`100%`, `12rem`). */
  width?: number | string;
  /** CSS height — number maps to px, string passes through. Default 16 px. */
  height?: number | string;
  /** Border radius — `pill`, `card`, or a px number. Default `card` (10 px). */
  radius?: 'pill' | 'card' | 'sm' | number;
  /** Optional className for parent layout overrides. */
  className?: string;
  /** Test hook. */
  testId?: string;
}

function resolveRadius(radius: SkeletonProps['radius']): string {
  if (typeof radius === 'number') return `${radius}px`;
  switch (radius) {
    case 'pill':
      return 'var(--r-pill)';
    case 'sm':
      return 'var(--r-sm)';
    case 'card':
    case undefined:
    default:
      return 'var(--r-md)';
  }
}

function resolveSize(value: number | string | undefined, fallback: string): string {
  if (value === undefined) return fallback;
  return typeof value === 'number' ? `${value}px` : value;
}

export function Skeleton({ width, height, radius, className, testId }: SkeletonProps) {
  const style: CSSProperties = {
    width: resolveSize(width, '100%'),
    height: resolveSize(height, '16px'),
    borderRadius: resolveRadius(radius),
  };
  return (
    <span
      className={`skeleton ${className ?? ''}`.trim()}
      style={style}
      data-testid={testId}
      aria-hidden="true"
    />
  );
}

/**
 * SkeletonCard — preset that matches the WorkList row dimensions
 * (16 px padding, ~74 px tall, three text lines + a status pill).
 * Used as the loading state for WorkList in spec 216 PR 1.
 */
export function SkeletonCard({ testId }: { testId?: string } = {}) {
  return (
    <div className="skeleton-card" data-testid={testId} aria-hidden="true">
      <div className="skeleton-card-body">
        <Skeleton width="60%" height={18} />
        <Skeleton width="40%" height={14} />
        <Skeleton width="48%" height={12} />
      </div>
      <Skeleton width={84} height={20} radius="pill" />
    </div>
  );
}

/**
 * SkeletonList — `count` SkeletonCard rows in a column. Used by
 * WorkList while the IDB read is in flight.
 */
export function SkeletonList({ count = 6, testId }: { count?: number; testId?: string }) {
  return (
    <div
      className="skeleton-list"
      data-testid={testId}
      role="status"
      aria-live="polite"
      aria-label="Cargando trabajos"
    >
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}
